/**
 * sanitizeClipboardHtml — nettoie du HTML collé depuis le presse-papier en ne
 * conservant QUE les tags <strong> et <em>. Tout le reste (couleurs, polices,
 * tailles, classes, attributs...) est supprimé mais le contenu textuel est
 * preserve.
 *
 * Détection du gras :
 *   - tag <b> ou <strong>
 *   - style inline font-weight: bold|bolder|>=600
 *
 * Détection de l'italique :
 *   - tag <i> ou <em>
 *   - style inline font-style: italic
 *
 * Les blocs (<p>, <div>, <li>, <h1>...) sont aplatis avec un espace pour
 * preserver la separation entre mots. Les multiples espaces consecutifs sont
 * normalises a 1.
 *
 * Utilise par RichInput.jsx et BlockEditor.jsx pour un comportement de paste
 * uniforme dans toute l'application.
 */
export function sanitizeClipboardHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const escape = (str) => {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    };
    const BLOCK_TAGS = new Set(['p', 'div', 'br', 'li', 'tr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            return escape(node.textContent || '');
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return '';
        }

        const tag = node.tagName.toLowerCase();
        const style = node.style || {};
        const fw = (style.fontWeight || '').toString();
        const fs = (style.fontStyle || '').toString();

        const isBold = tag === 'b' || tag === 'strong'
            || fw === 'bold' || fw === 'bolder'
            || (/^\d+$/.test(fw) && parseInt(fw, 10) >= 600);

        const isItalic = tag === 'i' || tag === 'em' || fs === 'italic';

        const children = Array.from(node.childNodes).map(walk).join('');
        let result = children;
        if (isItalic) result = `<em>${result}</em>`;
        if (isBold) result = `<strong>${result}</strong>`;

        if (BLOCK_TAGS.has(tag) && result && !result.endsWith(' ')) {
            result += ' ';
        }
        return result;
    };

    return Array.from(doc.body.childNodes).map(walk).join('').replace(/\s+/g, ' ').trim();
}

/**
 * Detecte si une chaine de texte plain contient des balises HTML <strong>/<em>/<b>/<i>
 * sous forme litterale (cas du copier-coller depuis un chat IA, un editeur Markdown,
 * ou tout autre source qui renvoie le HTML comme texte brut sans le mettre dans
 * le type MIME text/html du presse-papier).
 */
function plainTextContainsHtmlMarkup(text) {
    return /<(strong|em|b|i)\b[^>]*>/i.test(text);
}

/**
 * Handler de paste reutilisable pour les contentEditable.
 * Appelle e.preventDefault() et injecte le HTML nettoye via document.execCommand.
 * Retourne true si la valeur a ete inseree (utile pour callbacks onChange).
 *
 * Strategie de detection (par ordre de priorite) :
 *   1. Si clipboard.text/html present : sanitize le HTML.
 *   2. Si clipboard.text/plain contient des balises <strong>/<em>/<b>/<i>
 *      sous forme litterale : interprete le texte comme du HTML et sanitize.
 *   3. Sinon : texte plain pur, echappe pour insertion litterale.
 */
export function handleSmartPaste(e) {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const plain = e.clipboardData.getData('text/plain') || '';

    let toInsert;
    if (html && html.trim()) {
        toInsert = sanitizeClipboardHtml(html);
        if (!toInsert) {
            const div = document.createElement('div');
            div.textContent = plain;
            toInsert = div.innerHTML;
        }
    } else if (plainTextContainsHtmlMarkup(plain)) {
        // Cas frequent : copier-coller depuis un chat IA qui affiche les balises
        // <strong> en litteral. L'utilisateur attend l'interpretation du HTML.
        toInsert = sanitizeClipboardHtml(plain);
        if (!toInsert) {
            const div = document.createElement('div');
            div.textContent = plain;
            toInsert = div.innerHTML;
        }
    } else {
        const div = document.createElement('div');
        div.textContent = plain;
        toInsert = div.innerHTML;
    }

    // eslint-disable-next-line no-restricted-syntax
    document.execCommand('insertHTML', false, toInsert);
    return true;
}
